import moment, { Moment } from "moment"
import "moment/locale/cs"
import { CourseDates } from "./getCourseDates"
import { CourseVariant } from "../prismic/Course"

moment.locale("cs")

const weekdayAccusativeMap: Record<string, string> = {
  pondělí: "Každé pondělí",
  úterý: "Každé úterý",
  středa: "Každou středu",
  čtvrtek: "Každý čtvrtek",
  pátek: "Každý pátek",
  sobota: "Každou sobotu",
  neděle: "Každou neděli"
}

const getLocalizedWeekday = (date: Moment): string => {
  const weekday = date.format("dddd")
  return weekdayAccusativeMap[weekday] || weekday
}

const getStartAndEndTime = (firstCourseDay: Moment, lastCourseDay?: Moment): { startTime: string; endTime: string } | null => {
  if (firstCourseDay.hour() === 0 && firstCourseDay.minute() === 0) return null
  const end = lastCourseDay ?? firstCourseDay.clone().add(60, "minutes")
  return {
    startTime: firstCourseDay.format("HH:mm"),
    endTime: end.format("HH:mm")
  }
}

const getFormattedDateRange = (first: Moment, last?: Moment): { startDate: string; endDate: string } => {
  const endDateMoment = last ?? first
  return {
    startDate: first.format("D. M. YYYY"),
    endDate: endDateMoment.format("D. M. YYYY")
  }
}

const countIsoWeekRange = (first: Moment, last: Moment): number => {
  const startYear = first.isoWeekYear()
  const endYear = last.isoWeekYear()
  const startWeek = first.isoWeek()
  const endWeek = last.isoWeek()
  return startYear === endYear ? endWeek - startWeek + 1 : moment(`${startYear}-12-28`).isoWeeksInYear() - startWeek + 1 + endWeek
}

const getWeekLabel = (count: number): string => {
  if (count === 1) return "1 týden"
  if (count >= 2 && count <= 4) return `${count} týdny`
  return `${count} týdnů`
}

const weekdayLocativeMap: Record<string, string> = {
  pondělí: "v pondělí",
  úterý: "v úterý",
  středa: "ve středu",
  čtvrtek: "ve čtvrtek",
  pátek: "v pátek",
  sobota: "v sobotu",
  neděle: "v neděli"
}

const getWeekdayLocative = (date: Moment): string => {
  const weekday = date.format("dddd")
  return weekdayLocativeMap[weekday] || `v ${weekday}`
}

export const getCourseScheduleText = (
  courseDates: CourseDates,
  courseVariant: CourseVariant,
  firstCourseDay?: Moment,
  lastCourseDay?: Moment
): string | null => {
  if (!courseDates) return null

  const { lessonDates, lastLessonDate, skippedDates } = courseDates

  if (!lessonDates || lessonDates.length === 0 || !firstCourseDay) return null

  const first = lessonDates[0]

  if (
    (courseVariant === CourseVariant.sampleCourse && lessonDates.length === 1) ||
    (lastCourseDay && firstCourseDay.isSame(lastCourseDay ?? firstCourseDay, "day"))
  ) {
    const dayName = getWeekdayLocative(first)
    const dateText = first.format("D. M. YYYY")
    const timeRange = getStartAndEndTime(firstCourseDay, lastCourseDay)
    const timeText = timeRange ? ` ${timeRange.startTime} – ${timeRange.endTime}` : ` (čas bude upřesněn)`
    return `Jednorázový kurz ${dayName} ${dateText}${timeText}.`
  }

  const last = lastLessonDate
  const weekdayWithCorrectCase = getLocalizedWeekday(first)
  const timeRange = getStartAndEndTime(firstCourseDay, lastCourseDay)
  const { startDate, endDate } = getFormattedDateRange(first, lessonDates[lessonDates.length - 1])
  const totalWeeks = countIsoWeekRange(first, last)

  const skippedCount = skippedDates.length
  const skippedInfo = skippedCount > 0 ? `, bez ${skippedCount} státní${skippedCount === 1 ? "ho svátku" : "ch svátků"}` : ""

  const timeText = timeRange ? ` ${timeRange.startTime} – ${timeRange.endTime}` : ` (čas bude upřesněn)`

  return `${weekdayWithCorrectCase}${timeText} od ${startDate} do ${endDate} (${getWeekLabel(totalWeeks)}${skippedInfo}).`
}
